// /Users/pramod/Desktop/school-erp/frontend/src/Tenant/TenantWebsite/components/Hero.js

import React, { useEffect, useRef, useState } from 'react';
import { FiChevronRight as ChevronRight } from 'react-icons/fi';
import useBrandColors from '../utils/useBrandColors';

const Hero = ({
  hero = {},
  branding = {},
  apiBaseURL = '',
  onPrimaryAction = () => {},
  onSecondaryAction = () => {}
}) => {
  const heroRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();

  // Get hero data from props
  const {
    title,
    subtitle,
    description,
    backgroundImage,
    ctaText,
    ctaLink,
    secondaryCtaText,
    secondaryCtaLink
    } = hero;

  // Resolve the background image URL
  const getResolvedImageUrl = () => {
    if (!backgroundImage) return '';
    
    if (backgroundImage.startsWith('http://') || backgroundImage.startsWith('https://')) {
      return backgroundImage;
    }
    
    if (backgroundImage.startsWith('/')) {
      const base = apiBaseURL || '';
      return `${base}${backgroundImage}`;
    }
    
    return backgroundImage;
  };

  const resolvedImageUrl = getResolvedImageUrl();
  const hasBackgroundImage = resolvedImageUrl && resolvedImageUrl.trim() !== '' && !imageError;

  // Parallax effect for content
  useEffect(() => {
    const handleScroll = () => {
      if (!heroRef.current) return;
      const offset = window.scrollY;
      const opacity = Math.max(0, 1 - offset / 600);
      const translate = offset * 0.3;
      heroRef.current.style.transform = `translateY(${translate}px)`;
      heroRef.current.style.opacity = opacity;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handlers
  const handlePrimaryClick = () => {
    if (ctaLink && ctaLink !== '#') {
      if (ctaLink.startsWith('http://') || ctaLink.startsWith('https://')) {
        window.open(ctaLink, '_blank');
      } else if (ctaLink.startsWith('/')) {
        window.location.href = ctaLink;
      } else {
        window.open(`https://${ctaLink}`, '_blank');
      }
    }
    if (onPrimaryAction) onPrimaryAction();
  };

  const handleSecondaryClick = () => {
    if (secondaryCtaLink && secondaryCtaLink !== '#') {
      if (secondaryCtaLink.startsWith('http://') || secondaryCtaLink.startsWith('https://')) {
        window.open(secondaryCtaLink, '_blank');
      } else if (secondaryCtaLink.startsWith('/')) {
        window.location.href = secondaryCtaLink;
      } else {
        window.open(`https://${secondaryCtaLink}`, '_blank');
      }
    }
    if (onSecondaryAction) onSecondaryAction();
  };

  // Display values with fallbacks
  const displayTitle = title || branding?.title || 'Welcome to Our Institution';
  const displaySubtitle = subtitle || '';
  const displayDescription = description || '';
  const displayCtaText = ctaText || 'Explore Programs';
  const displaySecondaryCtaText = secondaryCtaText || 'Contact Admissions';

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ fontFamily: fontBody }}
    >
      {/* Background */}
      <div className="absolute inset-0">
        {hasBackgroundImage ? (
          // ─── DESIGN WITH BACKGROUND IMAGE ───
          <>
            {/* Background Image */}
            <div 
              className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 scale-105"
              style={{ 
                backgroundImage: `url(${resolvedImageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                opacity: imageLoaded ? 1 : 0
              }}
            >
              <img 
                src={resolvedImageUrl} 
                alt="Hero Background"
                className="hidden"
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              />
            </div>

            {/* Dark Overlay - Gradient from bottom and edges */}
            <div className="absolute inset-0 bg-gradient-to-t from-gray-950/90 via-gray-950/50 to-gray-950/30" />
            
            {/* Edge Vignette */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,rgba(0,0,0,0.4)_100%)]" />

            {/* Subtle Accent Glow - Using brand colors */}
            <div 
              className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse-slow"
              style={{ 
                backgroundColor: `${secondary}20`,
                animationDelay: '0s'
              }}
            />
            <div 
              className="absolute bottom-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse-slow"
              style={{ 
                backgroundColor: `${primary}20`,
                animationDelay: '2s'
              }}
            />
          </>
        ) : (
          // ─── DESIGN WITHOUT BACKGROUND IMAGE ───
          <>
            {/* Dark Background with brand gradient */}
            <div 
              className="absolute inset-0"
              style={{ 
                background: `linear-gradient(135deg, ${primary} 0%, ${primary}dd 30%, ${primary}99 60%, ${primary}66 100%)`
              }}
            />
            
            {/* Gradient Mesh */}
            <div 
              className="absolute inset-0"
              style={{ 
                background: `radial-gradient(circle at 30% 40%, ${secondary}20, transparent 50%)`,
              }}
            />
            <div 
              className="absolute inset-0"
              style={{ 
                background: `radial-gradient(circle at 70% 60%, ${primary}30, transparent 50%)`,
              }}
            />
            
            {/* Abstract Pattern */}
            <div className="absolute inset-0 opacity-[0.04]">
              <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                <defs>
                  <pattern id="hero-grid" width="8" height="8" patternUnits="userSpaceOnUse">
                    <path d="M 8 0 L 0 0 0 8" fill="none" stroke="white" strokeWidth="0.5" />
                  </pattern>
                </defs>
                <rect width="100" height="100" fill="url(#hero-grid)" />
              </svg>
            </div>

            {/* Decorative Orbs with brand colors */}
            <div 
              className="absolute top-20 left-10 w-96 h-96 rounded-full blur-3xl animate-pulse-slow"
              style={{ 
                backgroundColor: `${secondary}15`,
                animationDelay: '0s'
              }}
            />
            <div 
              className="absolute bottom-20 right-10 w-96 h-96 rounded-full blur-3xl animate-pulse-slow"
              style={{ 
                backgroundColor: `${secondary}10`,
                animationDelay: '2s'
              }}
            />
            <div 
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] rounded-full blur-3xl"
              style={{ 
                backgroundColor: `${primary}10`,
              }}
            />
          </>
        )}
      </div>

      {/* Content */}
      <div
        ref={heroRef}
        className="container mx-auto px-6 lg:px-12 relative z-10 py-20"
      >
        <div className="max-w-5xl mx-auto text-center">
          {/* Badge */}
          <div 

          >
            <span 
              className="w-1.5 h-1.5 rounded-full mr-3 animate-pulse"
              style={{ backgroundColor: secondary }}
            />
            <span 
              className={`text-[0.625rem] font-light tracking-[0.2em] uppercase ${
                hasBackgroundImage ? 'text-white/70' : 'text-white/60'
              }`}
            >
            </span>
          </div>

          {/* Title */}
          <h1 
            className={`font-bold text-white leading-[1.08] tracking-tight mb-6 transition-all duration-500 ${
              hasBackgroundImage ? 'drop-shadow-xl' : ''
            }`}
            style={{ fontFamily: fontHeading }}
          >
            <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
              {displayTitle}
            </span>
            {displaySubtitle && (
              <span 
                className={`block text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light mt-4 ${
                  hasBackgroundImage ? 'text-white/60' : 'text-white/50'
                }`}
                style={{ fontFamily: fontBody }}
              >
                {displaySubtitle}
              </span>
            )}
          </h1>

          {/* Description */}
          {displayDescription && (
            <p 
              className={`text-base sm:text-lg md:text-xl max-w-3xl mx-auto mb-10 leading-relaxed font-light ${
                hasBackgroundImage ? 'text-white/50' : 'text-white/40'
              }`}
              style={{ fontFamily: fontBody }}
            >
              {displayDescription}
            </p>
          )}

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            {/* Primary CTA - Using secondary color from branding */}
            <button
              onClick={handlePrimaryClick}
              className="group inline-flex items-center gap-3 px-8 py-3.5 rounded-full font-medium text-base transition-all duration-500 hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98]"
              style={{ 
                backgroundColor: secondary,
                color: '#ffffff',
                fontFamily: fontBody,
              }}
            >
              <span>{displayCtaText}</span>
              <ChevronRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>

            {/* Secondary CTA - Using primary color with transparency */}
            <button
              onClick={handleSecondaryClick}
              className="group inline-flex items-center gap-3 px-8 py-3.5 rounded-full font-medium text-base transition-all duration-500 hover:scale-[1.02] active:scale-[0.98]"
              style={{ 
                backgroundColor: hasBackgroundImage 
                  ? 'rgba(255,255,255,0.1)' 
                  : `${primary}40`,
                backdropFilter: 'blur(4px)',
                color: '#ffffff',
                border: `1px solid ${hasBackgroundImage ? 'rgba(255,255,255,0.2)' : `${secondary}40`}`,
                fontFamily: fontBody,
              }}
            >
              <span>{displaySecondaryCtaText}</span>
              <ChevronRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;